import {
  Box,
  Loader,
  Radio,
  Text,
  TextInput,
  Textarea,
  NumberInput
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { AiOutlineArrowRight } from 'react-icons/ai';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../utils/Button/index';
import { data } from './properties';
import { useStyles } from './style';
import { useDisclosure } from '@mantine/hooks';
import LanLordListPopup from './landLordListPopup';
import { baseURL } from '../../../config/urls';
import { doRedirectIfUnAuth, getHeader } from '../../../config/userSession';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { BiDollar } from 'react-icons/bi';

function AddNewSuperAdminPlan() {
  const { classes, theme } = useStyles(useStyles);
  const [opened, { close, open }] = useDisclosure(false);
  const [buttonClicked, setButtonClicked] = useState('');
  const [planType, setPlanType] = useState('');
  const [billingType, setBillingType] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [landlordLists, setLandlordLists] = useState([]);
  const [showLandlordListError, setShowLandlordListError] = useState('');
  const [planTypeError, setPlanTypeError] = useState('');
  const [billingTypeError, setBillingTypeError] = useState('');
  const [
    numberOfPropertyAllowedToListError,
    setNumberOfPropertyAllowedToListError
  ] = useState('');
  const navigate = useNavigate();

  const { titles } = data;

  const form = useForm({
    initialValues: {
      planName: '',
      planPrice: '',
      landlordList: [''],
      planDescription: '',
      numberOfPropertyAllowedToList: ''
    },
    validate: {
      planType: (value) =>
        value == ''
          ? data.placeholderError.planType[0]
          : /^[a-zA-Z]*$/.test(value)
          ? null
          : data.placeholderError.planType[1],
      billingType: (value) =>
        value == ''
          ? data.placeholderError.planBilling[0]
          : /^[a-zA-Z]*$/.test(value)
          ? null
          : data.placeholderError.planBilling[1],
      planName: (value) =>
        value == ''
          ? data.placeholderError.planName[0]
          : value.length < 3
          ? data.placeholderError.planName[1]
          : null,
      planPrice: (value) =>
        value == ''
          ? data.placeholderError.planPrice[0]
          : !/^(?!0*(\.0{1,2})?$)\d+(\.\d{1,2})?$/.test(value)
          ? data.placeholderError.planPrice[1]
          : null,
      landlordList: (value) =>
        value == '' && data.placeholderError.landlordList[0],
      planDescription: (value) =>
        value == ''
          ? data.placeholderError.planDescription[0]
          : value.length < 3
          ? data.placeholderError.planDescription[1]
          : value.length > 2500
          ? data.placeholderError.planDescription[2]
          : null
    }
  });

  const addTextField = () => {
    if (
      form.values.landlordList[0] !== '' &&
      /^\S+@\S+$/.test(form.values.landlordList) &&
      !landlordLists.includes(form.values.landlordList)
    ) {
      if (form.values.landlordList.length > 50) {
        setShowLandlordListError(
          'Email should be less than or equal to 50 characters'
        );
        return;
      } else {
        setShowLandlordListError('');
        landlordLists.push(form.values.landlordList);
        form.setValues((currentValues) => ({
          ...currentValues,
          landlordList: ['']
        }));
      }
    } else if (landlordLists.includes(form.values.landlordList)) {
      setShowLandlordListError('Email already added');
    } else {
      setShowLandlordListError('Invalid email');
    }
  };

  const handlePlanTypeChange = (event) => {
    setPlanType(event.currentTarget.value);
    setPlanTypeError('');
  };

  const handleBillingTypeChange = (event) => {
    setBillingType(event.currentTarget.value);
    setBillingTypeError('');
  };

  const showNotification = (notificationType, message, title, width) => {
    let colorCode = notificationType === 'success' ? 'secondary.0' : 'red';

    notifications.show({
      id: 'plan-notification',
      withCloseButton: true,
      title: title,
      message: message,
      autoClose: false,
      color: colorCode,
      icon:
        notificationType === 'success' ? (
          <IconCheck size="2.5rem" />
        ) : (
          <IconX size="2.5rem" />
        ),
      style: {
        backgroundColor: theme.colors.background[0],
        padding: '12px 12px',
        fontSize: '8rem',
        lineHeight: '1.2',
        minHeight: '120px',
        width: width, // 👈 Set desired width here
        maxWidth: '90%' // Optional: responsive on smaller screens
      },
      styles: {
        title: {
          fontSize: '1.5rem',
          lineHeight: 1.3
        },
        description: {
          fontSize: '1.2rem',
          lineHeight: 1.4
        },
        closeButton: {
          color: 'white',
          backgroundColor: 'black',
          '&:hover': {
            backgroundColor: 'gray', // Change to your preferred hover color
            color: 'yellow' // Example hover text color
          }
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      let error = false;
      if (planType === '') {
        error = true;
        setPlanTypeError('Plan Type is required');
      }
      if (billingType === '') {
        error = true;
        setBillingTypeError('Billing Type is required');
      }
      if (form.values.numberOfPropertyAllowedToList === '') {
        error = true;
        setNumberOfPropertyAllowedToListError(
          data.placeholderError.numberOfPropertyAllowedToList[0]
        );
      } else if (!/^[0-9]*$/.test(form.values.numberOfPropertyAllowedToList)) {
        error = true;
        setNumberOfPropertyAllowedToListError(
          data.placeholderError.numberOfPropertyAllowedToList[1]
        );
      }
      if (error) return;
      setNumberOfPropertyAllowedToListError('');
      if (form.isValid()) {
        if (
          landlordLists.length === 0 &&
          planType.toUpperCase() === 'PRIVATE'
        ) {
          setShowLandlordListError('Landlord list is required');
          return;
        }
        if (planType !== '' && billingType !== '') {
          setLoading(true);

          const formValues = {
            ...values,
            planType: planType.toUpperCase(),
            billingType: billingType.toUpperCase(),
            planStatus: buttonClicked === 'add' ? 'INACTIVE' : 'ACTIVE',
            landlordList: landlordLists,
            planPrice: parseFloat(form.values.planPrice).toFixed(2),
            numberOfPropertyAllowedToList: Number(
              form.values.numberOfPropertyAllowedToList
            )
          };
          const response = await fetch(
            `${baseURL}/superadmin/add-subscription-plan`,
            {
              method: 'POST',
              headers: getHeader(),
              body: JSON.stringify(formValues)
            }
          );
          if (response.ok) {
            navigate('/superadmin/plan');
            showNotification('success', 'Plan Added Successfully', '', '350px');
            form.reset();
          } else {
            setLoading(false);
            doRedirectIfUnAuth(response.status);
            let apiResponse = await response.text();

            try {
              var errorResponse = JSON.parse(apiResponse);
            } catch (error) {
              throw new Error(apiResponse);
            }
            showNotification('failure', 'Some Error occured', '', '350px');
          }
        }
      }
    } catch (error) {
      setLoading(false);
      showNotification('failure', error.message, '', '350px');
    }
  };
  const handleDeleteLandlord = (deletedItem) => {
    const filterEmails = landlordLists.filter((item) => item !== deletedItem);
    setLandlordLists(filterEmails);
    showNotification('success', 'Email deleted successfully', '', '350px');
  };

  const handleOpenPopup = () => {
    if (planType.toUpperCase() === 'PRIVATE') {
      open();
    } else {
      return;
    }
  };

  useEffect(() => {
    if (planType.toUpperCase() === 'PUBLIC') {
      setShowLandlordListError('');
      setLandlordLists([]);
    }
  }, [planType]);

  return (
    <Box className={classes.wrapperContainer}>
      <p className={classes.title}>{titles.addPlanList}</p>

      <form onSubmit={form.onSubmit(handleSubmit)} className={classes.wrapper}>
        <Box className={classes.editFormAndChangeStatus}>
          <Box className={classes.addFormContainer}>
            <Box className={classes.inputFields}>
              <Box className={classes.radioButtonInputWrapper}>
                <label className={classes.radioLabel}>{titles.planType}</label>
                <Box className={classes.radioOptionWrapper}>
                  {data.planType.map((type) => (
                    <Radio
                      key={type}
                      value={type}
                      label={type}
                      className={classes.radio}
                      checked={planType === type}
                      onChange={handlePlanTypeChange}
                      color="secondary.0"
                    />
                  ))}
                </Box>
              </Box>
              <p className={classes.emailError}>{planTypeError}</p>

              <Box className={classes.radioButtonInputWrapper}>
                <label className={classes.radioLabel}>
                  {titles.planBilling}
                </label>
                <Box className={classes.radioOptionWrapper}>
                  {data.planBillingType.map((type) => (
                    <Radio
                      key={type}
                      value={type}
                      label={type}
                      className={classes.radio}
                      checked={billingType === type}
                      onChange={handleBillingTypeChange}
                      color="secondary.0"
                    />
                  ))}
                </Box>
              </Box>
              <p className={classes.emailError}>{billingTypeError}</p>

              <Box className={classes.inputWrapper}>
                <label className={classes.label}>{titles.planName}</label>
                <TextInput
                  className={classes.textInput}
                  variant="unstyled"
                  placeholder="Plan Name"
                  {...form.getInputProps('planName')}
                />
              </Box>
              <Box className={classes.inputWrapper}>
                <label className={classes.label}>{titles.planPrice}</label>
                <TextInput
                  icon={<BiDollar className={classes.dollarIcon} />}
                  className={classes.textInputPrice}
                  variant="unstyled"
                  placeholder="0.00"
                  {...form.getInputProps('planPrice')}
                />
              </Box>
              <Box className={classes.inputWrapper}>
                <label className={classes.label}>
                  {titles.numberOfPropertyAllowedToList}
                </label>
                <NumberInput
                  className={classes.textInputNumber}
                  min={1}
                  placeholder="0"
                  {...form.getInputProps('numberOfPropertyAllowedToList')}
                  error={numberOfPropertyAllowedToListError}
                />
              </Box>
              <Box className={classes.inputWrapper}>
                <label className={classes.label}>{titles.landlordList}</label>

                <TextInput
                  className={classes.emailTextInput}
                  variant="unstyled"
                  placeholder="Email"
                  rightSection={
                    <Text
                      className={classes.addLandlordText}
                      onClick={addTextField}
                    >
                      Add
                    </Text>
                  }
                  disabled={planType === 'Public'}
                  {...form.getInputProps('landlordList')}
                />
                <p className={classes.emailError}>{showLandlordListError}</p>
              </Box>

              <Box className={classes.linkWrapper} onClick={handleOpenPopup}>
                {titles.viewListOfLandlord}
                <AiOutlineArrowRight className={classes.rightArrow} />
              </Box>
            </Box>
          </Box>

          <Box className={classes.textAreaWrapper}>
            <label className={classes.label}>{titles.planDescription}</label>
            <Textarea
              placeholder="Plan Description"
              {...form.getInputProps('planDescription')}
              className={classes.textarea}
            />
          </Box>
        </Box>

        <Box className={classes.actionBtns}>
          <Link to="/admin/plan" className={classes.cancel}>
            {titles.cancel}
          </Link>
          <Button
            type={'submit'}
            className={!isLoading ? classes.addBtn : classes.disabledBtnSmall}
            bgColor={
              !isLoading
                ? theme.colors.secondary[0]
                : theme.colors.disabledBtn[1]
            }
            title={titles.addButtonText}
            onClick={() => setButtonClicked('add')}
            disabled={isLoading}
          />
          <Button
            type={'submit'}
            className={!isLoading ? classes.btn : classes.disabledBtnLarge}
            bgColor={
              !isLoading ? theme.colors.primary[0] : theme.colors.disabledBtn[1]
            }
            title={titles.addAndActivate}
            onClick={() => setButtonClicked('activate')}
            disabled={isLoading}
          />
        </Box>
        <LanLordListPopup
          opened={opened}
          close={close}
          landlordLists={landlordLists}
          handleDeleteLandlord={handleDeleteLandlord}
        />
      </form>
    </Box>
  );
}

export default AddNewSuperAdminPlan;
